import type { Order, Vendor } from '@/lib/vision/schemas'

/**
 * Pulse v1 `counterparty.name` for Amazon sellingParty.partyId codes.
 * Verified 2 Sep 2026 from oms-po-ingest prod. Unknown codes stay the code —
 * never invent a pretty name.
 *
 * Twin of Vision's `VendorDisplayNames.LEGACY_BY_CODE`; the two must not
 * drift. Both were transcribed from the extract in Vision's
 * `scripts/seed_boldfit_main_vendor_names.sql`, which is the authority on
 * membership. `5r8bk` and `81hh0` were once here and are not on it — one
 * character off `5r88k` / `81hho` on the 8/B and O/0 pairs — and had no
 * bill-to, LWA account or PO anywhere, so both were dropped. Do not re-add a
 * code the extract does not list.
 *
 * This is a lookup table, not a list of vendors that exist. The dropdown is
 * built from what the API returns; see {@link vendorFilterOptions}.
 */
const KNOWN_VENDOR_NAMES: Record<string, string> = {
  k96zx: 'RetailEZ',
  jn6xt: 'RetailEZ',
  lbm4v: 'RetailEZ',
  kk8na: 'RetailEZ',
  lphah: 'RetailEZ',
  ppa9a: 'RetailEZ',
  up9e4: 'RetailEZ',
  uj954: 'RetailEZ',
  c67fv: 'RK Worldinfocom',
  s97v5: 'RK Worldinfocom',
  y27ua: 'Cocoblu',
  '5r88k': 'Cocoblu',
  t995v: 'Cocoblu',
  ul838: 'Cocoblu',
  uk796: 'Kay Kay Overseas',
  '81hho': 'Kay Kay Overseas',
  '34d5o': 'Kay Kay Overseas',
  d784t: 'Clicktech Retail',
  flipkart: 'Flipkart',
  flipkart_minutes: 'Flipkart Minutes',
}

export function knownVendorName(code: string): string {
  const key = code.trim().toLowerCase()
  return key ? (KNOWN_VENDOR_NAMES[key] ?? '') : ''
}

function resolvedName(name: string, code: string): string {
  const trimmed = name.trim()
  if (trimmed && trimmed.toLowerCase() !== code.trim().toLowerCase()) return trimmed
  return knownVendorName(code) || trimmed
}

/**
 * Pulse v1 Vendor label is `counterparty.name` (RetailEZ, Cocoblu, …).
 * Vision keeps Amazon's sellingParty.partyId on `order.vendor` and, after the
 * Vision hotfix, joins `vendors.display_name` onto `order.vendorName`.
 *
 * Render as `RetailEZ (K96ZX)` when name and code both exist and differ.
 * Fallback: API `vendorName` → directory `displayName` → v1 map → the Amazon
 * code. Treat a name that still equals the code as missing.
 */
export function vendorLabel(
  order: Pick<Order, 'vendor' | 'vendorName'>,
  vendors: Vendor[] = [],
): string {
  const code = order.vendor?.trim() || ''
  const fromApi = order.vendorName?.trim() || ''
  const fromDirectory = code
    ? vendors.find(v => v.code.toLowerCase() === code.toLowerCase())?.displayName?.trim() || ''
    : ''
  const name = resolvedName(fromApi || fromDirectory, code)
  if (code.toLowerCase() === 'flipkart') return name || 'Flipkart'
  if (code.toLowerCase() === 'flipkart_minutes') return name || 'Flipkart Minutes'
  return formatVendorNameCode(name, code)
}

/**
 * Dropdown options. Filter value stays the Amazon code. Label is
 * `RetailEZ (K96ZX)` so several RetailEZ selling-party rows stay distinct.
 *
 * Membership comes only from data: Vision's `GET /v1/vendors`, which is
 * already the directory unioned with `SELECT DISTINCT vendor` over the same
 * orders the queue reads, plus the vendors on the current page for the window
 * before that query settles.
 *
 * {@link KNOWN_VENDOR_NAMES} is consulted for *labelling* only, via
 * {@link resolvedName}. It used to be unioned into membership too, which put
 * every code it holds in the dropdown whether or not a PO referenced it —
 * selecting one filtered the queue to nothing. A name that is only ever needed
 * when a PO shows up can wait until one does.
 */
export function vendorFilterOptions(
  vendors: Vendor[],
  orders: Pick<Order, 'vendor' | 'vendorName'>[] = [],
): { code: string; displayName: string }[] {
  const byCode = new Map<string, { code: string; displayName: string }>()
  const add = (code: string, rawName: string) => {
    const trimmed = code.trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (byCode.has(key)) return
    const name = resolvedName(rawName, trimmed)
    byCode.set(key, { code: trimmed, displayName: formatVendorNameCode(name, trimmed) })
  }
  for (const vendor of vendors) {
    add(vendor.code, vendor.displayName)
  }
  for (const order of orders) {
    add(order.vendor ?? '', order.vendorName ?? '')
  }
  return [...byCode.values()].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, 'en', { sensitivity: 'base' }),
  )
}

function formatVendorNameCode(name: string, code: string): string {
  if (name && code && name !== code) return `${name} (${code})`
  return name || code
}
